/**
 * MyBrokerage — a brokerage_admin's own brokerage.
 *
 * The fields are live rather than read-only-behind-a-Modify-toggle: this is a
 * settings page for one row that only its owner can edit, and the round trip of
 * "read the value, press Modify, edit, press Done" bought nothing.
 *
 * Status and the custom domain are READ-ONLY here and come attached to
 * GET /admin/brokerage-settings. The /tenants routes that otherwise serve them
 * are super_admin-only, so this is a brokerage_admin's only view of either;
 * changing a status or registering a domain stays with the platform operator.
 *
 * The TXR-1501 numeric terms below are DEFAULTS a new agent's rate card starts
 * from (V061) — each agent's own card overrides them (Staff → Manage).
 */

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  brokerageSettingsService,
  uploadLogoFile,
} from "@re-platform/api-client";
import type { BrokerageSettings } from "@re-platform/api-client";
import {
  Card,
  PhoneInput,
  PlacesAutocomplete,
  Toast,
  type SelectedPlace,
} from "@re-platform/ui";

import { api } from "../../lib/api";
import { Collapsible } from "../../lib/cards";
import { formatPhone } from "../../lib/contacts";
import { useRoles } from "../../lib/roles";
import { ComplianceDocsSection } from "./ComplianceDocs";

const LOGO_MAX_BYTES = 1_048_576;
const LOGO_ALLOWED_EXT = ["png", "jpg", "jpeg", "svg", "webp"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-md)",
  background: "var(--rp-color-surface-2)",
  color: "var(--rp-color-text)",
  font: "inherit",
  fontSize: 14,
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: 6,
  fontSize: 13,
  fontWeight: 600,
  minWidth: 0,
};

const hintStyle: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 11.5,
  fontWeight: 400,
};

/** Two fields per row, collapsing to one when the card gets narrow. */
const fieldGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
};

/**
 * Read-only for the whole subtree.
 *
 * A context rather than a prop on each Field: there are fifteen of them, and
 * one missed call site is a field an agent can still type into. The write is
 * refused by brokerage-settings either way — this is so the screen does not
 * offer an edit that is going to fail.
 */
const ReadOnlyContext = createContext(false);

function Field({
  label,
  hint,
  value,
  placeholder,
  onChange,
  span,
  inputMode,
}: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  /** Full-width inside the grid — for an address line, not a ZIP. */
  span?: boolean;
  inputMode?: "decimal" | "numeric";
}) {
  const readOnly = useContext(ReadOnlyContext);
  return (
    <label
      style={{ ...labelStyle, ...(span ? { gridColumn: "1 / -1" } : null) }}
    >
      <span>
        {label}
        {hint && <span style={hintStyle}> — {hint}</span>}
      </span>
      <input
        value={value}
        placeholder={readOnly ? "—" : placeholder}
        inputMode={inputMode}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        style={
          readOnly
            ? { ...inputStyle, color: "var(--rp-color-text-muted)" }
            : inputStyle
        }
      />
    </label>
  );
}

/** `Field`, but it formats as a US phone and stores E.164. Read-only mode falls
 *  back to `Field`, which already renders a stored value verbatim. */
function PhoneField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const readOnly = useContext(ReadOnlyContext);
  if (readOnly) {
    return (
      <Field
        label={label}
        hint={hint}
        value={formatPhone(value)}
        onChange={onChange}
      />
    );
  }
  return (
    <label style={labelStyle}>
      <span>
        {label}
        {hint && <span style={hintStyle}> — {hint}</span>}
      </span>
      <PhoneInput
        className=""
        style={inputStyle}
        value={value}
        onChange={(e164) => onChange(e164 ?? "")}
      />
    </label>
  );
}

/**
 * `Field`, but the address line autocompletes and fills City/State/ZIP with it.
 *
 * A brokerage's office address is printed on the IABS and on every agreement
 * the brokerage sends, so it is worth getting right once rather than typing in
 * four boxes. Falls back to `Field`'s behaviour in read-only mode and whenever
 * there is no API key — the sibling boxes stay editable either way.
 */
function AddressField({
  label,
  value,
  placeholder,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onPick: (p: SelectedPlace) => void;
}) {
  const readOnly = useContext(ReadOnlyContext);
  if (readOnly) {
    return (
      <Field
        label={label}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        span
      />
    );
  }
  return (
    <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
      <span>{label}</span>
      <PlacesAutocomplete
        apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        className=""
        style={inputStyle}
        value={value}
        onChange={onChange}
        onSelect={onPick}
        placeholder={placeholder}
      />
    </label>
  );
}

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  ACTIVE: {
    bg: "var(--rp-color-success-faint)",
    fg: "var(--rp-color-success)",
  },
  PENDING: {
    bg: "var(--rp-color-warning-faint)",
    fg: "var(--rp-color-warning)",
  },
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? {
    bg: "var(--rp-color-surface-2)",
    fg: "var(--rp-color-text-muted)",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 14px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.3,
        background: tone.bg,
        color: tone.fg,
      }}
    >
      {status || "—"}
    </span>
  );
}

/** The route: a staff member's own brokerage. */
export function MyBrokerage() {
  const roles = useRoles();
  // An agent reads it; the admins edit it. `isAgent` alone is not the test — a
  // broker who also carries the agent role must keep their edit rights.
  const readOnly =
    roles.isAgent && !roles.isBrokerageAdmin && !roles.isSuperAdmin;
  return (
    <>
      <h1 style={{ marginTop: 0, marginBottom: 4 }}>My brokerage</h1>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          marginTop: 0,
          marginBottom: 20,
        }}
      >
        {readOnly
          ? "Your brokerage's details — these are the terms and identity your own agreements are written on. Your broker maintains them."
          : "Your brokerage profile — this branding and these details appear on client-facing pages and TREC forms."}
      </p>
      <BrokerageProfile readOnly={readOnly} />
    </>
  );
}

/**
 * A brokerage's whole profile, for whoever is allowed to edit it.
 *
 * Mounted twice: bare on My brokerage (a broker's own, `tenantId` undefined),
 * and inside the platform admin's tenant Manage panel with a `tenantId`. Every
 * call underneath already took an optional tenant id, so the only thing that
 * had to change was passing it.
 *
 * The platform admin previously got a DIFFERENT form for the same row — one
 * flat card with nine inputs on a single line. Same data, same permissions,
 * two layouts, and the cramped one was the screen you actually provision a new
 * brokerage from.
 *
 * `statusExtra` is how the two differ where they genuinely should: a broker
 * reads their domain and is told the operator manages it, while the operator
 * gets the real controls (add, Check DNS, remove) in that same slot.
 */
export function BrokerageProfile({
  tenantId,
  statusExtra,
  readOnly = false,
}: {
  /** Undefined = the caller's own brokerage. */
  tenantId?: string;
  /** Replaces the read-only note in the Status card. */
  statusExtra?: React.ReactNode;
  /** Show the profile without any way to change it (an agent's view). */
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<BrokerageSettings>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryKey = ["brokerage-settings", tenantId ?? "own"];
  const q = useQuery({
    queryKey,
    queryFn: () => brokerageSettingsService.get(api, tenantId),
  });

  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: Partial<BrokerageSettings>) =>
      brokerageSettingsService.update(api, body, tenantId),
    onSuccess: (saved) => {
      setDraft(saved);
      void queryClient.invalidateQueries({ queryKey });
      setToast("Brokerage profile saved.");
    },
    onError: (err) =>
      setToast(err instanceof Error ? err.message : "Save failed"),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!LOGO_ALLOWED_EXT.includes(ext)) {
        throw new Error(`Use one of ${LOGO_ALLOWED_EXT.join(", ")}`);
      }
      if (file.size > LOGO_MAX_BYTES) {
        throw new Error("The logo must be 1MB or smaller");
      }
      const presigned = await brokerageSettingsService.presignLogoUpload(
        api,
        ext,
        tenantId,
      );
      await uploadLogoFile(presigned.upload_url, presigned.content_type, file);
      return brokerageSettingsService.confirmLogoUpload(
        api,
        presigned.s3_key,
        tenantId,
      );
    },
    onSuccess: (saved) => {
      setDraft(saved);
      void queryClient.invalidateQueries({ queryKey });
      setToast("Logo uploaded.");
    },
    onError: (err) =>
      setToast(err instanceof Error ? err.message : "Logo upload failed"),
  });

  const set = (key: keyof BrokerageSettings) => (v: string) =>
    setDraft((d) => ({ ...d, [key]: v }));
  const val = (key: keyof BrokerageSettings) => String(draft[key] ?? "");

  if (q.isError) {
    return (
      <p style={{ color: "var(--rp-color-danger)" }}>
        {q.error instanceof Error
          ? q.error.message
          : "Could not load the brokerage"}
      </p>
    );
  }

  const isMortgage = draft.brokerage_type === "MORTGAGE";
  // Market area is REQUIRED to activate but lives in a collapsed section, so a
  // tenant that is not live yet opens it — otherwise the operator is told the
  // profile is incomplete and the missing field is behind a "+".
  const pending = draft.status === "PENDING";

  return (
    <ReadOnlyContext.Provider value={readOnly}>
      {/* Details are the long column; branding and status are short and stack
          beside them, dropping underneath once there is no room for two. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        {/* 2:1 while there is room for both, stacked once the sidebar can no
            longer hold 280px. A grid with auto-fit columns cannot express that
            ratio without a media query, which an inline style cannot carry. */}
        <Card padding="lg" style={{ flex: "2 1 420px", minWidth: 0 }}>
          <Collapsible title="Company details">
            <div style={fieldGrid}>
              <Field
                label="Legal name"
                hint="prints on every agreement"
                value={val("legal_name")}
                placeholder="Lone Star Realty LLC"
                onChange={set("legal_name")}
              />
              {/* Read-only: display_name lives on the tenant, and renaming a
                brokerage is a platform-operator action. */}
              <label style={labelStyle}>
                <span>
                  Display name
                  <span style={hintStyle}> — set by the platform</span>
                </span>
                <input
                  value={draft.display_name ?? ""}
                  readOnly
                  style={{
                    ...inputStyle,
                    color: "var(--rp-color-text-muted)",
                    cursor: "not-allowed",
                  }}
                />
              </label>
              {/* One licence — the one this brokerage can actually hold. */}
              {isMortgage ? (
                <Field
                  label="NMLS #"
                  value={val("nmls_id")}
                  placeholder="1029384"
                  onChange={set("nmls_id")}
                />
              ) : (
                <Field
                  label="TREC broker license #"
                  value={val("trec_license_number")}
                  placeholder="480091"
                  onChange={set("trec_license_number")}
                />
              )}
              {/* The IABS names these two BY LAW, on their own rows — the
                  firm's licence above is neither of them. Without them the
                  disclosure goes out with blanks where the broker should be. */}
              {!isMortgage && (
                <>
                  <Field
                    label="Designated broker"
                    hint="the person, not the firm"
                    value={val("designated_broker_name")}
                    placeholder="Dana Whitfield"
                    onChange={set("designated_broker_name")}
                  />
                  <Field
                    label="Designated broker licence #"
                    value={val("designated_broker_license_no")}
                    placeholder="480091"
                    onChange={set("designated_broker_license_no")}
                  />
                  {/* Their OWN email and phone. The notice prints these on the
                      broker's row; until they existed it printed the firm's
                      main line there, on a disclosure that names the person. */}
                  <Field
                    label="Designated broker email"
                    hint="theirs, not the firm's — required to activate"
                    value={val("designated_broker_email")}
                    placeholder="dana@brokerage.com"
                    onChange={set("designated_broker_email")}
                  />
                  <PhoneField
                    label="Designated broker phone"
                    hint="theirs, not the firm's — required to activate"
                    value={val("designated_broker_phone")}
                    onChange={set("designated_broker_phone")}
                  />
                  <Field
                    label="Supervising licensee"
                    hint="leave blank if this is the designated broker"
                    value={val("supervising_license_holder")}
                    placeholder="Same as designated broker"
                    onChange={set("supervising_license_holder")}
                  />
                  <Field
                    label="Supervisor licence #"
                    hint="leave blank to use the broker's"
                    value={val("supervising_license_no")}
                    placeholder="Same as designated broker"
                    onChange={set("supervising_license_no")}
                  />
                  {/* Once a supervisor IS named, their blank fields render
                      blank on the notice rather than borrowing the broker's —
                      so the hint must not promise a fallback that only applies
                      when nobody is named at all. */}
                  <Field
                    label="Supervisor email"
                    hint="blank prints blank once a supervisor is named"
                    value={val("supervising_email")}
                    placeholder="Same as designated broker"
                    onChange={set("supervising_email")}
                  />
                  <PhoneField
                    label="Supervisor phone"
                    hint="blank prints blank once a supervisor is named"
                    value={val("supervising_phone")}
                    onChange={set("supervising_phone")}
                  />
                </>
              )}
              <PhoneField
                label="Main phone"
                value={val("primary_phone")}
                onChange={set("primary_phone")}
              />
              <Field
                label="Support email"
                value={val("support_email")}
                placeholder="hello@lonestar.com"
                onChange={set("support_email")}
              />
              <AddressField
                label="Office address"
                value={val("address_street")}
                placeholder="1200 S Congress Ave"
                onChange={set("address_street")}
                onPick={(p) => {
                  // Street only — City/State/ZIP have their own boxes, and the
                  // formatted address would repeat them into the street line.
                  set("address_street")(p.street || p.address);
                  if (p.city) set("address_city")(p.city);
                  if (p.state) set("address_state")(p.state);
                  if (p.zip) set("address_zip")(p.zip);
                }}
              />
              <Field
                label="City"
                value={val("address_city")}
                placeholder="Austin"
                onChange={set("address_city")}
              />
              <Field
                label="State"
                value={val("address_state")}
                placeholder="TX"
                onChange={set("address_state")}
              />
              <Field
                label="ZIP"
                value={val("address_zip")}
                placeholder="78704"
                onChange={set("address_zip")}
              />
            </div>
            {!readOnly && (
              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  className="rp-btn rp-btn--primary"
                  disabled={save.isPending || q.isLoading}
                  onClick={() => save.mutate(draft)}
                >
                  {save.isPending ? "Saving…" : "Save brokerage profile"}
                </button>
              </div>
            )}
          </Collapsible>
        </Card>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            flex: "1 1 280px",
            minWidth: 0,
          }}
        >
          <Card padding="lg">
            <Collapsible title="Branding">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "var(--rp-radius-lg)",
                    border: "1px solid var(--rp-color-border)",
                    background: "var(--rp-color-surface-2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                    fontSize: 10,
                    color: "var(--rp-color-text-muted)",
                  }}
                >
                  {draft.logo_url ? (
                    <img
                      src={draft.logo_url}
                      alt="Brokerage logo"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    "No logo"
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Logo</div>
                  {readOnly ? (
                    <div style={hintStyle}>Set by your broker.</div>
                  ) : (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={LOGO_ALLOWED_EXT.map((e) => `.${e}`).join(",")}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadLogo.mutate(file);
                          e.target.value = "";
                        }}
                      />
                      <button
                        type="button"
                        disabled={uploadLogo.isPending}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: "none",
                          background: "none",
                          padding: 0,
                          font: "inherit",
                          fontSize: 13.5,
                          color: "var(--rp-color-accent, var(--rp-color-text))",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {uploadLogo.isPending
                          ? "Uploading…"
                          : draft.logo_url
                            ? "Replace"
                            : "Upload"}
                      </button>
                      <div style={{ ...hintStyle, marginTop: 2 }}>
                        PNG/JPEG/SVG/WEBP, up to 1MB.
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginTop: 16,
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    cursor: readOnly ? "default" : "pointer",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      width: 56,
                      height: 56,
                      borderRadius: "var(--rp-radius-md)",
                      border: "1px solid var(--rp-color-border)",
                      background:
                        draft.primary_color ?? "var(--rp-color-surface-2)",
                    }}
                  />
                  {!readOnly && (
                    <input
                      type="color"
                      value={draft.primary_color ?? "#1a2b3c"}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          primary_color: e.target.value,
                        }))
                      }
                      style={{
                        position: "absolute",
                        width: 0,
                        height: 0,
                        opacity: 0,
                      }}
                    />
                  )}
                </label>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    Brand color
                  </div>
                  <code
                    style={{
                      fontSize: 13,
                      color: "var(--rp-color-text-muted)",
                    }}
                  >
                    {draft.primary_color ?? "not set"}
                  </code>
                  <div style={{ ...hintStyle, marginTop: 2 }}>
                    {readOnly
                      ? "Set by your broker."
                      : "Saved with the profile."}
                  </div>
                </div>
              </div>
            </Collapsible>
          </Card>

          <Card padding="lg">
            <Collapsible title="Status">
              <StatusPill status={draft.status ?? ""} />
              <div style={{ marginTop: 12, fontSize: 13.5 }}>
                <span style={{ color: "var(--rp-color-text-muted)" }}>
                  Custom domain:{" "}
                </span>
                {draft.custom_domain ? (
                  <>
                    <code style={{ fontSize: 13 }}>{draft.custom_domain}</code>{" "}
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: draft.custom_domain_verified
                          ? "var(--rp-color-success-faint)"
                          : "var(--rp-color-warning-faint)",
                        color: draft.custom_domain_verified
                          ? "var(--rp-color-success)"
                          : "var(--rp-color-warning)",
                      }}
                    >
                      {draft.custom_domain_verified ? "verified" : "unverified"}
                    </span>
                  </>
                ) : (
                  <span style={{ color: "var(--rp-color-text-muted)" }}>
                    none yet
                  </span>
                )}
              </div>
              {statusExtra ?? (
                <p style={{ ...hintStyle, marginTop: 10, marginBottom: 0 }}>
                  Status and domains are managed by the platform operator.
                </p>
              )}
            </Collapsible>
          </Card>
        </div>
      </div>

      {/* TXR-1501 is a Texas real estate form; a mortgage brokerage originates
          no representation agreements, so none of this applies to one. */}
      {!isMortgage && (
        <Card padding="lg" style={{ marginTop: 16 }}>
          <Collapsible
            title="Agreement defaults"
            defaultOpen={pending}
            subtitle="What a new agent's rate card starts from. Each agent can override these on their own card (Staff → Manage)."
          >
            <div style={fieldGrid}>
              <Field
                label="Market area"
                hint="¶3C, should not be blank"
                value={val("market_area")}
                placeholder="Dallas-Fort Worth Metroplex"
                onChange={set("market_area")}
              />
              <Field
                label="County"
                hint="¶7H — REQUIRED before any listing agreement can be sent"
                value={val("county")}
                placeholder="Travis"
                onChange={set("county")}
              />
              <Field
                label="Purchase fee"
                hint="¶7A1, % of the sales price"
                value={val("purchase_fee_pct")}
                inputMode="decimal"
                onChange={set("purchase_fee_pct")}
              />
              <Field
                label="Lease fee"
                hint="¶7A2, % of one month's rent"
                value={val("lease_fee_pct")}
                inputMode="decimal"
                onChange={set("lease_fee_pct")}
              />
              {/* TXR 1102 ¶5.E — LEASE LISTING agreements only. Blank INHERITS
                  (D28): the renewal rate falls back to the lease fee above and
                  the tenant-sale rate to the purchase fee, because a renewal is
                  a lease and a sale to the tenant is a sale. Set one only to
                  charge something different. Blank is not "no charge" — enter 0
                  for that, which is a stated rate and beats the fallback. */}
              <Field
                label="Renewal fee"
                hint="¶5.E(1), % of one month's rent — blank inherits the lease fee"
                value={val("renewal_fee_pct")}
                inputMode="decimal"
                onChange={set("renewal_fee_pct")}
              />
              <Field
                label="Tenant sale fee"
                hint="¶5.E(2), % of the sales price if the tenant buys — blank inherits the purchase fee"
                value={val("sale_fee_pct")}
                inputMode="decimal"
                onChange={set("sale_fee_pct")}
              />
              <Field
                label="Protection period"
                hint="¶7F, days"
                value={val("protection_period_days")}
                inputMode="numeric"
                onChange={set("protection_period_days")}
              />
              <Field
                label="Term length"
                hint="¶4, months from the day it is sent"
                value={val("representation_term_months")}
                inputMode="numeric"
                onChange={set("representation_term_months")}
              />
            </div>
            {!readOnly && (
              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  className="rp-btn rp-btn--primary"
                  disabled={save.isPending || q.isLoading}
                  onClick={() => save.mutate(draft)}
                >
                  {save.isPending ? "Saving…" : "Save agreement defaults"}
                </button>
              </div>
            )}
          </Collapsible>
        </Card>
      )}

      {/* The BROKERAGE's documents. compliance-docs pins an agent caller to
          their OWN rows, so for them this section would list something else
          entirely under a heading that says otherwise.

          Hidden for a mortgage brokerage: the only two kinds here are the IABS
          and the Consumer Protection Notice, both TREC real-estate forms that a
          lender neither files nor sends. The server agrees — it requires an IABS
          of every brokerage EXCEPT a mortgage one. */}
      {!readOnly && !isMortgage && (
        <Card padding="lg" style={{ marginTop: 16 }}>
          <Collapsible title="Compliance documents" defaultOpen={pending}>
            <ComplianceDocsSection tenantId={tenantId} />
          </Collapsible>
        </Card>
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </ReadOnlyContext.Provider>
  );
}
